import * as React from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormField,
    Popover,
    SpaceBetween,
    Textarea,
    TextContent
} from "@amzn/awsui-components-react";
import { FeedbackRating, FeedbackType, IPageFeedback, EntityFactory } from "@amzn/ask-legal-domain";
import { AppContext } from "../../../setup/context";
import { useAPI } from "../../../hooks/api-hook";
import { StarRating } from "../../common/StarRating";

interface PageFeedbackModalProps {
    pageId: string;
    instanceId: string;
    visible: boolean;
    onDismiss: () => void;
    initialRating?: FeedbackRating | null;
    pageRef?: any; // EntityRef for the page
    selectedRating?: FeedbackRating | null; // The rating that triggered the modal
}

export const PageFeedbackModal: React.FC<PageFeedbackModalProps> = ({
    pageId,
    instanceId,
    visible,
    onDismiss,
    pageRef,
    selectedRating
}) => {
    const context = React.useContext(AppContext);
    const [rating, setRating] = React.useState<FeedbackRating | null>(null);
    const [feedbackTypes, setFeedbackTypes] = React.useState<FeedbackType[]>([]);
    const [comments, setComments] = React.useState<string>("");
    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [showFeedbackForm, setShowFeedbackForm] = React.useState<boolean>(false);

    const submitFeedbackRunner = useAPI(context.getPageFeedbackAPI().submit);
    React.useEffect(() => {
        if (visible && selectedRating) {
            setRating(selectedRating);
            setShowFeedbackForm(true);
        }
    }, [visible, selectedRating]);

    const handleFeedbackTypeChange = (type: FeedbackType, checked: boolean) => {
        if (checked) {
            setFeedbackTypes(prev => [...prev, type]);
        } else {
            setFeedbackTypes(prev => prev.filter(t => t !== type));
        }
    };

    const handleSubmit = async (
        submitRating: FeedbackRating = rating,
        submitFeedbackTypes: FeedbackType[] = feedbackTypes,
        submitComments: string = comments
    ) => {
        if (!submitRating || !pageRef) return;

        try {
            const input = IPageFeedback.SubmitFeedbackInput.create({
                pageRef: pageRef,
                rating: submitRating,
                feedbackTypes: submitFeedbackTypes.length > 0 ? submitFeedbackTypes : undefined,
                comments: submitComments.trim() || undefined,
                sessionId: `session-${Date.now()}`,
                url: window.location.href
            });

            await submitFeedbackRunner.submitRun(input);
            setSubmitted(true);
        } catch (error) {
            console.error("Failed to submit feedback:", error);
        }
    };

    const handleClose = () => {
        setRating(null);
        setFeedbackTypes([]);
        setComments("");
        setSubmitted(false);
        setShowFeedbackForm(false);
        onDismiss();
    };

    if (!visible) return null;

    return (
        <div style={{
            position: "absolute",
            top: "100%",
            left: "0",
            zIndex: 1000,
            backgroundColor: "white",
            border: "1px solid #d5dbdb",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            padding: "16px",
            minWidth: "400px",
            maxWidth: "500px"
        }}>
            <div style={{ position: "relative" }}>
                <div style={{
                    position: "absolute",
                    top: "-8px",
                    right: "-8px"
                }}>
                    <Button
                        variant="icon"
                        iconName="close"
                        onClick={handleClose}
                        ariaLabel="Close feedback form"
                    />
                </div>

                {submitted ? (
                    <Box textAlign="center">
                        <SpaceBetween direction="vertical" size="m">
                            <TextContent>
                                <h3>Thank you for your feedback!</h3>
                                <p>Your feedback helps us improve the content quality.</p>
                            </TextContent>
                        </SpaceBetween>
                    </Box>
                ) : (
                    <SpaceBetween direction="vertical" size="m">
                        <TextContent>
                            <h3>Page Content Feedback</h3>
                        </TextContent>

                        {showFeedbackForm && (
                            <>
                                {rating <= FeedbackRating.THREE_STARS &&
                                    <FormField label="What did you dislike about this page?">
                                        <SpaceBetween direction="vertical" size="xs">
                                            <Checkbox
                                                checked={feedbackTypes.includes(FeedbackType.COMPLETENESS)}
                                                onChange={({ detail }) =>
                                                    handleFeedbackTypeChange(FeedbackType.COMPLETENESS, detail.checked)
                                                }
                                            >
                                                <strong>Incomplete</strong> - Page content is incomplete
                                            </Checkbox>
                                            <Checkbox
                                                checked={feedbackTypes.includes(FeedbackType.ACCURACY)}
                                                onChange={({ detail }) =>
                                                    handleFeedbackTypeChange(FeedbackType.ACCURACY, detail.checked)
                                                }
                                            >
                                                <strong>Out-of-Date</strong> - Page content is out of date
                                            </Checkbox>
                                            <Checkbox
                                                checked={feedbackTypes.includes(FeedbackType.OTHER)}
                                                onChange={({ detail }) =>
                                                    handleFeedbackTypeChange(FeedbackType.OTHER, detail.checked)
                                                }
                                            >
                                                <strong>Broken Links</strong> - Page has broken links
                                            </Checkbox>
                                            <Checkbox
                                                checked={feedbackTypes.includes(FeedbackType.USEFULNESS)}
                                                onChange={({ detail }) =>
                                                    handleFeedbackTypeChange(FeedbackType.USEFULNESS, detail.checked)
                                                }
                                            >
                                                <strong>Doesn"t answer my question</strong> - Page does not answer my question
                                            </Checkbox>
                                        </SpaceBetween>
                                    </FormField>
                                }

                                <FormField
                                    label={rating <= FeedbackRating.THREE_STARS ? "Please tell us how can we improve the page?" : "Please tell us what you liked about this page?"}
                                >
                                    <Textarea
                                        value={comments}
                                        onChange={({ detail }) => setComments(detail.value)}
                                        placeholder=""
                                        rows={4}
                                    />
                                </FormField>

                                <Box float="right">
                                    <SpaceBetween direction="horizontal" size="xs">
                                        <Button
                                            variant="link"
                                            onClick={handleClose}
                                            disabled={submitFeedbackRunner.status === "Running"}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={() => handleSubmit()}
                                            disabled={!rating || submitFeedbackRunner.status === "Running"}
                                            loading={submitFeedbackRunner.status === "Running"}
                                        >
                                            Confirm
                                        </Button>
                                    </SpaceBetween>
                                </Box>
                            </>
                        )}

                        {submitFeedbackRunner.status === "Error" && (
                            <Alert type="error">
                                Failed to submit feedback. Please try again.
                            </Alert>
                        )}
                    </SpaceBetween>
                )}
            </div>
        </div>
    );
};
